import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";

// Scales only the main content pane (see MainLayout.tsx), never the
// sidebar/header -- those were just resized to a deliberate fixed size, and
// re-growing them here would undo that. This exists because shrinking the
// sidebar made the (unchanged) page content look comparatively larger, so
// users can dial the content back down -- or up -- to taste.
const STORAGE_KEY = "contentScale";
const MIN_SCALE = 70;
const MAX_SCALE = 130;
const STEP = 10;
const DEFAULT_SCALE = 100;

interface ContentScaleContextValue {
  scale: number;
  increase: () => void;
  decrease: () => void;
  reset: () => void;
  canIncrease: boolean;
  canDecrease: boolean;
}

const ContentScaleContext = createContext<ContentScaleContextValue>({
  scale: DEFAULT_SCALE,
  increase: () => {},
  decrease: () => {},
  reset: () => {},
  canIncrease: true,
  canDecrease: true,
});

const getInitialScale = (): number => {
  if (typeof window === "undefined") return DEFAULT_SCALE;
  const stored = Number(window.localStorage.getItem(STORAGE_KEY));
  if (stored && stored >= MIN_SCALE && stored <= MAX_SCALE) return stored;
  return DEFAULT_SCALE;
};

export const ContentScaleProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [scale, setScale] = useState<number>(getInitialScale);

  const persist = useCallback((next: number) => {
    window.localStorage.setItem(STORAGE_KEY, String(next));
    setScale(next);
  }, []);

  const increase = useCallback(() => {
    setScale((prev) => {
      const next = Math.min(MAX_SCALE, prev + STEP);
      window.localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const decrease = useCallback(() => {
    setScale((prev) => {
      const next = Math.max(MIN_SCALE, prev - STEP);
      window.localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const reset = useCallback(() => persist(DEFAULT_SCALE), [persist]);

  const value = useMemo(
    () => ({
      scale,
      increase,
      decrease,
      reset,
      canIncrease: scale < MAX_SCALE,
      canDecrease: scale > MIN_SCALE,
    }),
    [scale, increase, decrease, reset],
  );

  return (
    <ContentScaleContext.Provider value={value}>
      {children}
    </ContentScaleContext.Provider>
  );
};

export const useContentScale = () => useContext(ContentScaleContext);
